<template>
  <v-card :loading="isLoading">
    <v-card-text v-if="clusterId">
      <v-data-table
        v-bind="dataTableAttrs"
        :headers="headersShown"
        :items="list"
        :loading="isLoading"
        :options.sync="options"
        :server-items-length="total"
        :items-per-page.sync="itemsPerPage"
        v-model="selectedItems"
        @update:items-per-page="handleItemsPerPageUpdated"
      >
        <template v-slot:top>
          <rs-table-title-bar
            v-if="$refs.headSearch"
            title="Demirbaşlar"
            icon="mdi-folder-star-multiple"
            :add-route="
              can('edit-fixture')
                ? {
                    name: 'other-definitions.fixtures.fixtures.create',
                  }
                : null
            "
            @clear-filters="$refs.headSearch.reset"
            @reload="loadList"
            :hide-edit="!can('edit-fixture')"
            :edit-enabled="selectedItems.length === 1"
            :search.sync="search.query"
            @click:edit="handleEditClick"
            @click:delete="handleDeleteClick"
            :show-delete="can('delete-fixture')"
            :delete-enabled="selectedItems.length === 1"
          />
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:body.prepend="{ headers }">
          <rs-table-head-search
            :headers="headers"
            :search.sync="search"
            ref="headSearch"
          />
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.edit="{ item }">
          <router-link
            class="btn btn-icon btn-sm btn-clean"
            :to="{
              name: 'other-definitions.fixtures.fixtures.edit',
              params: { id: item.id },
            }"
          >
            <i class="menu-icon mdi mdi-pencil"></i>
          </router-link>
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.name="{ item }">
          <router-link
            :to="{
              name: 'other-definitions.fixtures.fixtures.show',
              params: { id: item.id },
            }"
          >
            {{ item.name }}
          </router-link>
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.provider="{ item }">
          <router-link
            v-if="item.provider_id && can('see-cluster-provider')"
            :to="{
              name: 'definitions.cluster-providers.show',
              params: { id: item.provider_id },
            }"
          >
            {{ item.provider }}
          </router-link>
          <template v-else>
            {{ item.provider }}
          </template>
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.unit_price="{ value }">
          <rs-table-cell-number price :value="value" />
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.total_price="{ value }">
          <rs-table-cell-number price :value="value" />
        </template>
      </v-data-table>

      <rs-confirm
        ref="confirmDelete"
        :loading="isLoading"
        @confirmed="handleDelete"
        @cancelled="$refs.confirmDelete.hide()"
      />
    </v-card-text>

    <v-card-text v-else>
      Listeyi görmek için üst menüden bir toplu yaşam alanı seçin.
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";

import { debounce } from "@/core/plugins/debounce";
import { filtersToURL, hasPermissions } from "@/view/mixins";

export default {
  mixins: [filtersToURL, hasPermissions],
  computed: {
    ...mapGetters(["clusterId", "fixtureStatusList", "fixtureGroupsList"]),
  },
  watch: {
    options: {
      handler() {
        this.setURLParams();
      },
      deep: true,
    },
    search: {
      handler: debounce(function () {
        this.options.page = 1;
        this.setURLParams();
      }, 250),
      deep: true,
    },
    clusterId: {
      handler() {
        this.loadList();
      },
    },
  },
  data() {
    return {
      headers: [
        {
          text: "",
          value: "edit",
          searchable: false,
          sortable: false,
          width: "30px",
          align: "center",
          hide: () => !this.can("edit-fixture"),
        },
        {
          text: "Demirbaş Adı",
          value: "name",
          searchable: "text",
        },
        {
          text: "Demirbaş Grubu",
          value: "fixture_group",
          searchable: "multiselect",
          options: () => this.fixtureGroupsList,
        },
        {
          text: "Miktar",
          value: "quantity",
          searchable: "number",
        },
        {
          text: "Birim Fiyat",
          value: "unit_price",
          searchable: "number",
        },
        {
          text: "Toplam Fiyat",
          value: "total_price",
          searchable: "number",
        },
        {
          text: "Tedarikçi",
          value: "provider",
          searchable: "text",
        },
        {
          text: "Alınma Tarihi",
          value: "purchased_on",
          searchable: "date",
        },
        {
          text: "Garanti Başlangıç Tarihi",
          value: "warranty_starts_on",
          searchable: "date",
          width: "140px",
        },
        {
          text: "Garanti Bitiş Tarihi",
          value: "warranty_ends_on",
          searchable: "date",
          width: "140px",
        },
        {
          text: "Belge No (Fatura-Fiş No)",
          value: "document_number",
          searchable: "text",
          width: "140px",
        },
        {
          text: "Durumu",
          value: "fixture_status",
          searchable: "multiselect",
          options: () => this.fixtureStatusList,
        },
      ],
    };
  },
  methods: {
    loadList() {
      this.selectedItems = [];
      if (this.isLoading || !this.clusterId) {
        return;
      }

      this.isLoading = true;
      this.list = [];

      const params = Object.assign(
        this.buildSearchParams(this.search, this.options)
      );

      params.cluster_id = this.clusterId;

      this.$api
        .query(`/clusters/${this.clusterId}/fixtures/`, { params })
        .then((response) => {
          if (response.data && response.data.data) {
            this.list = response.data.data;
          }

          if (response.data && response.data.meta) {
            this.total = response.data.meta.total;
            this.options.page = response.data.meta.current_page;
            this.itemsPerPage = response.data.meta.per_page;
            this.options.itemsPerPage = this.itemsPerPage;
          }
        })
        .catch((error) => {
          // eslint-disable-next-line
          this.handleError(error);
        })
        .finally(() => (this.isLoading = false));
    },
    handleEditClick() {
      if (this.selectedItems.length === 1) {
        this.$router.push({
          name: "other-definitions.fixtures.fixtures.edit",
          params: { id: this.selectedItems[0].id },
        });
      }
    },
    handleDelete() {
      this.isLoading = true;

      this.$api
        .delete(`fixtures/${this.selectedItems[0].id}`)
        .then(() => {
          this.$toast.success("Silindi");
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => {
          this.isLoading = false;
          this.$nextTick(() => this.loadList());
        });
      this.$refs.confirmDelete.hide();
    },
    handleDeleteClick() {
      this.$refs.confirmDelete.show(
        `${this.selectedItems[0].name} adlı demirbaşı silmek istediğinizden emin misiniz?`
      );
    },
  },
};
</script>
